import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";
import utils from "../utils/utils";

function getApiDataset(response) {
  if (!response || typeof response !== "object") return null;
  const d = response.data ?? response.Data;
  return d && typeof d === "object" ? d : null;
}

function getFirstAvailableTable(data, candidates = []) {
  if (!data || typeof data !== "object") return null;
  const keys = Object.keys(data);
  for (const key of candidates) {
    const val = data[key];
    if (Array.isArray(val)) return val;
    const ci = keys.find(
      (k) => k.toLowerCase() === String(key || "").toLowerCase(),
    );
    if (ci && Array.isArray(data[ci])) return data[ci];
  }
  const firstArrayKey = keys.find((k) => Array.isArray(data[k]));
  return firstArrayKey ? data[firstArrayKey] : null;
}

export function extractSuccessStory(response, tableName = "SuccessStory") {
  const ds = getApiDataset(response);
  if (!ds) {
    if (Array.isArray(response?.SuccessStory)) return response.SuccessStory;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  const named = getFirstAvailableTable(ds, [tableName, "SuccessStory", "Table"]);
  if (Array.isArray(named)) return named;

  if (Array.isArray(ds)) return ds;

  if (Array.isArray(ds?.Tables)) {
    const table = ds.Tables.find(
      (t) =>
        t?.TableName === tableName ||
        t?.Name === tableName ||
        t?.tableName === tableName,
    );
    if (Array.isArray(table?.Rows)) return table.Rows;
    const first = ds.Tables.find((t) => Array.isArray(t?.Rows) && t.Rows.length);
    if (first?.Rows) return first.Rows;
  }

  return [];
}

export function firstSuccessStoryRow(response) {
  const rows = extractSuccessStory(response);
  return rows.length ? rows[0] : null;
}

export function isProcSuccess(row) {
  if (row == null) return false;
  const success = row.Success ?? row.success;
  return (
    Number(success) === 1 ||
    success === true ||
    String(success).toLowerCase() === "true"
  );
}

export function isProcFailure(row) {
  if (row == null) return false;
  const success = row.Success ?? row.success;
  return (
    Number(success) === 0 ||
    success === false ||
    String(success).toLowerCase() === "false"
  );
}

function resolveApiError(response, row, fallback) {
  const rowMsg = row?.MSG || row?.Msg || row?.message || row?.Message;
  if (rowMsg) return String(rowMsg).trim();

  return utils.getApiResponseMessage(response, fallback);
}

function categoryBasePayload(userData, overrides = {}) {
  const payload = {
    DocumentCategory: "",
    Abberiviation: "",
    OrganizationId: userData?.OrganizationId || "",
    CreatedBy: userData?.StaffId || "",
    ActionType: "",
    ...overrides,
  };

  const categoryId = String(payload.DocumentCategoryId || "").trim();
  if (categoryId) {
    payload.DocumentCategoryId = categoryId;
  } else {
    delete payload.DocumentCategoryId;
  }

  const staffRoles = String(payload.StaffRoles || "").trim();
  if (staffRoles) {
    payload.StaffRoles = staffRoles;
  } else {
    delete payload.StaffRoles;
  }

  return payload;
}

export async function fetchDocumentCategories(userData) {
  const response = await api.post(
    API_ENDPOINTS.QMS.DOCUMENT_CATEGORY_ACTIVITY,
    categoryBasePayload(userData, { ActionType: "GetAllDocumentCategory" }),
  );

  if (Number(response?.success) === 0 && !extractSuccessStory(response).length) {
    throw new Error(
      resolveApiError(
        response,
        null,
        "Unable to load document categories from server.",
      ),
    );
  }

  return extractSuccessStory(response);
}

export async function saveDocumentCategory(userData, payload) {
  const body = categoryBasePayload(userData, payload);

  const response = await api.post(
    API_ENDPOINTS.QMS.DOCUMENT_CATEGORY_ACTIVITY,
    body,
  );
  const row = firstSuccessStoryRow(response);
  const procOk = isProcSuccess(row);
  const procFail = isProcFailure(row);
  const ok =
    procOk || (Number(response?.success) === 1 && !procFail && row == null);

  if (!ok) {
    throw new Error(
      resolveApiError(response, row, "Could not save document category."),
    );
  }

  return {
    row,
    message:
      row?.MSG ||
      row?.Msg ||
      (payload?.DocumentCategoryId
        ? "Document category has been updated successfully."
        : "Document category has been saved successfully."),
  };
}

export async function deleteDocumentCategory(userData, documentCategoryId) {
  const response = await api.post(
    API_ENDPOINTS.QMS.DOCUMENT_CATEGORY_ACTIVITY,
    categoryBasePayload(userData, {
      ActionType: "DeleteDocumentCategory",
      DocumentCategoryId: documentCategoryId,
    }),
  );
  const row = firstSuccessStoryRow(response);

  if (!isProcSuccess(row)) {
    throw new Error(
      resolveApiError(response, row, "Failed to delete document category."),
    );
  }

  return {
    row,
    message:
      row?.MSG ||
      row?.Msg ||
      "Document category has been deleted successfully.",
  };
}
